import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import ReactWebChat, { createStore } from "botframework-webchat";
import {
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Attach24Regular } from "@fluentui/react-icons";
import { useAuth } from "../contexts/AuthContext";
import {
  initializeCopilotClient,
  createWebChatConnection,
} from "../copilot-client";
import { fetchRetrievalExtracts } from "../retrieval";
import {
  uploadFile,
  isFileTypeAllowed,
  isFileSizeAllowed,
  formatFileSize,
  MAX_FILE_SIZE,
  ALLOWED_FILE_TYPES,
  getFileIcon,
  type UploadedFile,
  type UploadProgress,
} from "../file-upload";
import { setCopilotChatRef } from "./ChatLayout";

// ── Tracked messages for save ────────────────────────
interface TrackedMessage {
  role: "user" | "bot";
  text: string;
  timestamp: string;
}

interface ContextFormValues {
  region?: string;
  solutionType?: string;
  broker?: string;
  client?: string;
}

const REGION_OPTIONS = ["UK", "US", "WEMEA", "HK", "Global"] as const;
const SOLUTION_TYPE_OPTIONS = ["CAPTIVE", "POOL"] as const;
const CUSTOM_OPTION = "__custom__";

function normalizeText(text: string): string {
  return text.replace(/\s+/g, " ").trim().toLowerCase();
}

function makeContextPromptKey(activity: any): string {
  return (
    activity?.id ||
    [activity?.timestamp || "", activity?.text || "", activity?.from?.id || ""]
      .filter(Boolean)
      .join("|")
  );
}

function isContextRequestMessage(text: string | undefined): boolean {
  if (!text) {
    return false;
  }

  const normalized = normalizeText(text);
  const hasFieldLabels = ["region", "solution type", "broker", "client"].every(
    (field) => normalized.includes(field),
  );
  const hasContextCue =
    normalized.includes("context") ||
    normalized.includes("to provide the most accurate") ||
    normalized.includes("to find the best answer") ||
    normalized.includes("to find the best match");

  return hasFieldLabels && hasContextCue;
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "hidden",
    position: "relative",
  },
  webchat: {
    flex: 1,
    overflow: "hidden",
    padding: "0 8px 8px 8px",
    "& > *": { height: "100%" },
    "& ol, & ul": { paddingLeft: "2em", margin: "0.4em 0" },
    "& li": { marginBottom: "0.15em" },
  },
  center: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
  },
  chipContainer: {
    display: "flex",
    gap: "0.25rem",
    flexWrap: "wrap",
    padding: "0.25rem 1rem",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.25rem",
    padding: "2px 8px",
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: "4px",
    fontSize: "12px",
  },
  chipRemove: {
    cursor: "pointer",
    border: "none",
    background: "none",
    padding: "0 2px",
    lineHeight: 1,
  },
  uploadProgress: {
    padding: "0.5rem 1rem",
    fontSize: "12px",
  },
  dragOver: {
    outline: `2px dashed ${tokens.colorBrandBackground}`,
    outlineOffset: "-4px",
  },
  attachToolbar: {
    display: "flex",
    alignItems: "center",
    padding: "4px 12px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  contextPromptWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  contextFormCard: {
    marginTop: "4px",
    marginLeft: "8px",
    padding: "12px",
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    maxWidth: "540px",
  },
  contextFormTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: "8px",
  },
  contextFormGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "10px 12px",
  },
  contextFormField: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  contextFormLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
  },
  contextFormInput: {
    width: "100%",
    minHeight: "34px",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    padding: "0 10px",
    boxSizing: "border-box",
    fontSize: tokens.fontSizeBase200,
    outlineStyle: "none",
    ":focus": {
      border: `1px solid ${tokens.colorBrandStroke1}`,
      boxShadow: `0 0 0 1px ${tokens.colorBrandStroke1}`,
    },
  },
  contextFormHint: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  contextFormActions: {
    display: "flex",
    gap: "8px",
    marginTop: "12px",
    flexWrap: "wrap",
  },
  contextFormButtonPrimary: {
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    fontWeight: tokens.fontWeightSemibold,
    minHeight: "34px",
    padding: "0 14px",
    cursor: "pointer",
  },
  contextFormButtonSecondary: {
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightMedium,
    minHeight: "34px",
    padding: "0 14px",
    cursor: "pointer",
  },
});

// ── Typing heartbeat ─────────────────────────────
let typingHeartbeatInterval: ReturnType<typeof setInterval> | null = null;

function startTypingHeartbeat(dispatch: (a: any) => void): void {
  stopTypingHeartbeat();
  const sendTyping = () =>
    dispatch({
      type: "DIRECT_LINE/INCOMING_ACTIVITY",
      payload: {
        activity: {
          type: "typing",
          from: { id: "copilot-agent", role: "bot" },
          timestamp: new Date().toISOString(),
        },
      },
    });
  sendTyping();
  typingHeartbeatInterval = setInterval(sendTyping, 3000);
}

function stopTypingHeartbeat(): void {
  if (typingHeartbeatInterval !== null) {
    clearInterval(typingHeartbeatInterval);
    typingHeartbeatInterval = null;
  }
}

// ── Style options (matches old WebChat theme) ─────────
const styleOptions = {
  backgroundColor: "#f7f7f8",
  bubbleBackground: "#ffffff",
  bubbleFromUserBackground: "#0078d4",
  bubbleFromUserTextColor: "#ffffff",
  bubbleBorderRadius: 8,
  bubbleFromUserBorderRadius: 8,
  bubbleMaxWidth: 900,
  bubbleMinWidth: 100,
  sendBoxBackground: "#ffffff",
  sendBoxButtonColor: "#0078d4",
  sendBoxButtonColorOnHover: "#005a9e",
  sendBoxTextColor: "#323130",
  sendBoxHeight: 52,
  sendBoxBorderTop: "1px solid #e0e0e0",
  hideUploadButton: true,
  rootHeight: "100%",
  rootWidth: "100%",
  transcriptOverlayButtonBackground: "rgba(0, 0, 0, 0.6)",
  transcriptOverlayButtonBackgroundOnHover: "rgba(0, 0, 0, 0.8)",
  suggestedActionsLayout: "stacked" as const,
  suggestedActionsStackedHeight: undefined,
  suggestedActionsStackedOverflow: "visible" as const,
};

function ContextPromptForm({
  promptKey,
  onSubmit,
  onSkip,
}: {
  promptKey: string;
  onSubmit: (promptKey: string, values: ContextFormValues) => void;
  onSkip: (promptKey: string) => void;
}) {
  const styles = useStyles();
  const [regionChoice, setRegionChoice] = useState("");
  const [regionCustom, setRegionCustom] = useState("");
  const [solutionChoice, setSolutionChoice] = useState("");
  const [solutionCustom, setSolutionCustom] = useState("");
  const [broker, setBroker] = useState("");
  const [client, setClient] = useState("");

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const region =
        regionChoice === CUSTOM_OPTION ? regionCustom.trim() : regionChoice;
      const solutionType =
        solutionChoice === CUSTOM_OPTION
          ? solutionCustom.trim()
          : solutionChoice;

      onSubmit(promptKey, {
        region: region || undefined,
        solutionType: solutionType || undefined,
        broker: broker.trim() || undefined,
        client: client.trim() || undefined,
      });
    },
    [
      broker,
      client,
      onSubmit,
      promptKey,
      regionChoice,
      regionCustom,
      solutionChoice,
      solutionCustom,
    ],
  );

  return (
    <form className={styles.contextFormCard} onSubmit={handleSubmit}>
      <div className={styles.contextFormTitle}>Add context for this search</div>

      <div className={styles.contextFormGrid}>
        <div className={styles.contextFormField}>
          <label
            className={styles.contextFormLabel}
            htmlFor={`${promptKey}-region`}
          >
            Region
          </label>
          <select
            id={`${promptKey}-region`}
            className={styles.contextFormInput}
            value={regionChoice}
            onChange={(event) => setRegionChoice(event.target.value)}
          >
            <option value="">Select region</option>
            {REGION_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
            <option value={CUSTOM_OPTION}>Other</option>
          </select>
          {regionChoice === CUSTOM_OPTION && (
            <input
              className={styles.contextFormInput}
              placeholder="Enter another region"
              value={regionCustom}
              onChange={(event) => setRegionCustom(event.target.value)}
            />
          )}
        </div>

        <div className={styles.contextFormField}>
          <label
            className={styles.contextFormLabel}
            htmlFor={`${promptKey}-solution-type`}
          >
            Solution Type
          </label>
          <select
            id={`${promptKey}-solution-type`}
            className={styles.contextFormInput}
            value={solutionChoice}
            onChange={(event) => setSolutionChoice(event.target.value)}
          >
            <option value="">Select solution type</option>
            {SOLUTION_TYPE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
            <option value={CUSTOM_OPTION}>Other</option>
          </select>
          {solutionChoice === CUSTOM_OPTION && (
            <input
              className={styles.contextFormInput}
              placeholder="Enter another solution type"
              value={solutionCustom}
              onChange={(event) => setSolutionCustom(event.target.value)}
            />
          )}
        </div>

        <div className={styles.contextFormField}>
          <label
            className={styles.contextFormLabel}
            htmlFor={`${promptKey}-broker`}
          >
            Broker
          </label>
          <input
            id={`${promptKey}-broker`}
            className={styles.contextFormInput}
            placeholder="e.g. WTW, AON, Marsh"
            value={broker}
            onChange={(event) => setBroker(event.target.value)}
          />
          <div className={styles.contextFormHint}>Optional free text</div>
        </div>

        <div className={styles.contextFormField}>
          <label
            className={styles.contextFormLabel}
            htmlFor={`${promptKey}-client`}
          >
            Client
          </label>
          <input
            id={`${promptKey}-client`}
            className={styles.contextFormInput}
            placeholder="Client company name"
            value={client}
            onChange={(event) => setClient(event.target.value)}
          />
          <div className={styles.contextFormHint}>Optional free text</div>
        </div>
      </div>

      <div className={styles.contextFormActions}>
        <button className={styles.contextFormButtonPrimary} type="submit">
          Search with Context
        </button>
        <button
          className={styles.contextFormButtonSecondary}
          type="button"
          onClick={() => onSkip(promptKey)}
        >
          Skip
        </button>
      </div>
    </form>
  );
}

export function CopilotChat() {
  const { acquireToken } = useAuth();
  const styles = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [directLine, setDirectLine] = useState<any>(null);
  const [store, setStore] = useState<any>(null);
  const [resolvedContextPrompts, setResolvedContextPrompts] = useState<
    Set<string>
  >(() => new Set());

  // File state
  const [pendingFiles, setPendingFiles] = useState<UploadedFile[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{
    name: string;
    pct: number;
  } | null>(null);
  const pendingFilesRef = useRef<UploadedFile[]>([]);
  pendingFilesRef.current = pendingFiles;

  // Message tracking
  const messagesRef = useRef<TrackedMessage[]>([]);

  // Drag state
  const [dragging, setDragging] = useState(false);
  const dragCounter = useRef(0);

  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Register ChatRef for ChatLayout ────────────
  useEffect(() => {
    setCopilotChatRef({
      getMessages: () => [...messagesRef.current],
      hasMessages: () => messagesRef.current.length > 0,
    });
    return () => setCopilotChatRef(null);
  }, []);

  const sendUserMessage = useCallback(
    (text: string) => {
      if (!store || !text.trim()) {
        return;
      }

      store.dispatch({
        type: "WEB_CHAT/SEND_MESSAGE",
        payload: { text },
      });
    },
    [store],
  );

  const markContextPromptResolved = useCallback((promptKey: string) => {
    setResolvedContextPrompts((prev) => {
      const next = new Set(prev);
      next.add(promptKey);
      return next;
    });
  }, []);

  const handleContextPromptSubmit = useCallback(
    (promptKey: string, values: ContextFormValues) => {
      const lines = ["RFP context:"];

      if (values.region) {
        lines.push(`Region: ${values.region}`);
      }
      if (values.solutionType) {
        lines.push(`Solution Type: ${values.solutionType}`);
      }
      if (values.broker) {
        lines.push(`Broker: ${values.broker}`);
      }
      if (values.client) {
        lines.push(`Client: ${values.client}`);
      }

      markContextPromptResolved(promptKey);
      sendUserMessage(lines.length > 1 ? lines.join("\n") : "skip context");
    },
    [markContextPromptResolved, sendUserMessage],
  );

  const handleContextPromptSkip = useCallback(
    (promptKey: string) => {
      markContextPromptResolved(promptKey);
      sendUserMessage("skip context");
    },
    [markContextPromptResolved, sendUserMessage],
  );

  const activityMiddleware = useMemo(
    () =>
      () =>
      (next: any) =>
      (...args: any[]) => {
        const [card] = args;
        const { activity } = card;

        if (activity?.channelData?.__suppressDisplay) {
          return () => null;
        }

        let nextCard = card;

        if (
          activity?.from?.role === "user" &&
          activity?.channelData?.__displayText
        ) {
          nextCard = {
            ...card,
            activity: { ...activity, text: activity.channelData.__displayText },
          };
        } else if (activity?.from?.role === "user" && activity?.text) {
          const blobUrlPattern =
            /URL: https:\/\/[a-z0-9]+\.blob\.core\.windows\.net\/[^\s]+/gi;
          if (blobUrlPattern.test(activity.text)) {
            nextCard = {
              ...card,
              activity: {
                ...activity,
                text: activity.text.replace(
                  /\nURL: https:\/\/[a-z0-9]+\.blob\.core\.windows\.net\/[^\s]+/gi,
                  "",
                ),
              },
            };
          }
        }

        const renderer = next(nextCard);

        if (
          nextCard.activity?.from?.role !== "user" &&
          isContextRequestMessage(nextCard.activity?.text)
        ) {
          const promptKey = makeContextPromptKey(nextCard.activity);

          if (!resolvedContextPrompts.has(promptKey)) {
            return (...renderArgs: any[]) => (
              <div className={styles.contextPromptWrap}>
                {renderer(...renderArgs)}
                <ContextPromptForm
                  key={promptKey}
                  promptKey={promptKey}
                  onSubmit={handleContextPromptSubmit}
                  onSkip={handleContextPromptSkip}
                />
              </div>
            );
          }
        }

        return renderer;
      },
    [
      handleContextPromptSkip,
      handleContextPromptSubmit,
      resolvedContextPrompts,
      styles.contextPromptWrap,
    ],
  );

  // ── Initialize DirectLine connection ─────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await acquireToken();
        const client = initializeCopilotClient(token);
        const dl = createWebChatConnection(client, { showTyping: true });
        if (cancelled) return;

        // Build store with middleware
        const s = createStore(
          {},
          ({ dispatch }: any) =>
            (next: any) =>
            (action: any) => {
              // ── SearchRetrieval interception ────────────
              if (action.type === "DIRECT_LINE/INCOMING_ACTIVITY") {
                const activity = action.payload?.activity;

                if (
                  activity?.type === "event" &&
                  activity?.name === "SearchRetrieval"
                ) {
                  const rawValue = activity.value;
                  const query = (
                    typeof rawValue === "string" ? rawValue : rawValue?.query
                  ) as string | undefined;

                  if (query) {
                    (async () => {
                      try {
                        const extracts = await fetchRetrievalExtracts(query);
                        dl.postActivity({
                          type: "event",
                          name: "SearchRetrieval",
                          value: { results: extracts ?? "" },
                          from: { id: "user", role: "user" },
                        }).subscribe();
                      } catch {
                        dl.postActivity({
                          type: "event",
                          name: "SearchRetrieval",
                          value: { results: "" },
                          from: { id: "user", role: "user" },
                        }).subscribe();
                      }
                    })();
                  }
                  return; // Don't pass event activities through
                }

                if (activity?.type === "message" && activity?.text) {
                  if (activity.from?.role !== "user") {
                    stopTypingHeartbeat();
                    messagesRef.current.push({
                      role: "bot",
                      text: activity.text,
                      timestamp: activity.timestamp || new Date().toISOString(),
                    });
                  }
                }
              }

              // ── Outgoing message enrichment ────────────
              if (action.type === "WEB_CHAT/SEND_MESSAGE") {
                if ((action as any).__enrichmentDone) {
                  return next(action);
                }

                const userText = (action.payload?.text || "") as string;
                const capturedFiles = [...pendingFilesRef.current];

                if (capturedFiles.length > 0) {
                  setPendingFiles([]);
                }

                startTypingHeartbeat(dispatch);

                (async () => {
                  let augmentedText = userText;

                  if (capturedFiles.length > 0) {
                    const fileInfos = capturedFiles
                      .map(
                        (f) =>
                          `${getFileIcon(f.contentType)} ${f.fileName} (${formatFileSize(f.size)})\nURL: ${f.url}`,
                      )
                      .join("\n\n");
                    augmentedText = augmentedText
                      ? `${augmentedText}\n\n---\nAttached files:\n${fileInfos}`
                      : `Attached files:\n${fileInfos}`;
                  }

                  const cleanTrackText = (() => {
                    if (capturedFiles.length > 0) {
                      const cleanFiles = capturedFiles
                        .map(
                          (f) =>
                            `${getFileIcon(f.contentType)} ${f.fileName} (${formatFileSize(f.size)})`,
                        )
                        .join("\n");
                      return userText
                        ? `${userText}\n\n---\nAttached files:\n${cleanFiles}`
                        : `Attached files:\n${cleanFiles}`;
                    }
                    return userText;
                  })();

                  if (cleanTrackText) {
                    messagesRef.current.push({
                      role: "user",
                      text: cleanTrackText,
                      timestamp: new Date().toISOString(),
                    });
                  }

                  dispatch({
                    ...action,
                    __enrichmentDone: true,
                    payload: {
                      ...action.payload,
                      text: augmentedText,
                      channelData: {
                        ...(action.payload?.channelData ?? {}),
                        __displayText: cleanTrackText || userText,
                      },
                    },
                  });
                })();

                return;
              }

              return next(action);
            },
        );

        setDirectLine(dl);
        setStore(s);
        setLoading(false);
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || "Failed to connect");
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      stopTypingHeartbeat();
    };
  }, [acquireToken]);

  // ── File upload handler ─────────────────────────
  const handleFileUpload = useCallback(async (file: File) => {
    if (!isFileTypeAllowed(file)) {
      setError(`File type '${file.type || "unknown"}' is not supported.`);
      return;
    }
    if (!isFileSizeAllowed(file)) {
      setError(
        `File is too large. Maximum size is ${formatFileSize(MAX_FILE_SIZE)}.`,
      );
      return;
    }
    setUploadProgress({ name: file.name, pct: 0 });
    try {
      const uploaded = await uploadFile(file, (p: UploadProgress) => {
        setUploadProgress({ name: file.name, pct: p.percentage });
      });
      setUploadProgress(null);
      setPendingFiles((prev) => [...prev, uploaded]);
    } catch (err: any) {
      setUploadProgress(null);
      setError(err.message || "Failed to upload file");
    }
  }, []);

  // ── Drag and drop ──────────────────────────────
  const onDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current++;
    setDragging(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) setDragging(false);
  }, []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      dragCounter.current = 0;
      setDragging(false);
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        handleFileUpload(files[0]);
      }
    },
    [handleFileUpload],
  );

  // ── Render ─────────────────────────────────────
  if (loading) {
    return (
      <div className={styles.center}>
        <Spinner size="large" label="Connecting to agent..." />
      </div>
    );
  }

  if (error && !directLine) {
    return (
      <div className={styles.center}>
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`${styles.root} ${dragging ? styles.dragOver : ""}`}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {error && (
        <MessageBar intent="error" style={{ flexShrink: 0 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.webchat}>
        <ReactWebChat
          directLine={directLine}
          store={store}
          styleOptions={styleOptions}
          activityMiddleware={activityMiddleware}
          locale="en-US"
        />
      </div>

      <div className={styles.attachToolbar}>
        <Button
          appearance="transparent"
          icon={<Attach24Regular />}
          onClick={() => fileInputRef.current?.click()}
          size="small"
          title="Attach a file"
        />
      </div>

      {uploadProgress && (
        <div className={styles.uploadProgress}>
          📤 Uploading: {uploadProgress.name} — {uploadProgress.pct}%
        </div>
      )}

      {pendingFiles.length > 0 && (
        <div className={styles.chipContainer}>
          {pendingFiles.map((f, i) => (
            <span key={i} className={styles.chip}>
              {getFileIcon(f.contentType)} {f.fileName}
              <button
                className={styles.chipRemove}
                onClick={() =>
                  setPendingFiles((prev) => prev.filter((_, j) => j !== i))
                }
                title="Remove"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Hidden file input for upload button */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_FILE_TYPES.join(",")}
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileUpload(file);
          e.target.value = "";
        }}
      />
    </div>
  );
}
